import {
  FlowNodeBaseType,
  type FreeLayoutPluginContext,
  type WorkflowNodeEntity,
} from '@flowgram.ai/free-layout-editor';
import { nanoid } from 'nanoid';
import type { FlowNodeData, FlowNodeMcpInfo } from '$/domains/workFlowV3/type';
import { useLanguageStore } from '$/store/languageStore';
import useFlowNextStore from '$/store/useTeamFlowStore';
import { getAllNodesBounds } from '$/utils/nodeAddPosition';
import {
  NODE_NAME_CONFIG,
  NODE_TYPE_TO_MODEL_TYPE_MAP,
  TVFlowNodeNextTypeEnum,
  type ModelNodeType,
} from '../config/node.config';
import {
  createDefaultDirectorConsoleState,
  createDirectorConsoleDataPatch,
} from '../DirectorConsole/state';
import type { DirectorConsoleNodeData } from '../DirectorConsole/types';
import {
  autoMatchAndUpdateNode,
  deriveInputTypeCounts,
  findMatchingToolInModel,
  getMediaModelsByNodeType,
  isAutoMatchMediaNodeType,
  isToolCompatible,
} from './autoMatchModelTool';
import { getInitialNodeMcpData } from './modelConfig';
import { inheritCommonMcpInfo, inheritCommonParameters } from './ModelMcpInfo';
import { getNextNodeNameByCurrentType } from './nodeNameCounter';

export interface TextNodeMediaItem extends Record<string, any> {
  task_id?: string;
  url?: string;
  value?: string;
  watermark_url?: string;
  cover_url?: string;
  watermark_cover_url?: string;
  width?: number;
  height?: number;
  duration?: number;
}

export interface TextNodeMcpInfo extends Record<string, any> {
  api_name: string;
  model_id?: string;
  node_model_id?: string;
  model_name?: string;
  parameters?: Record<string, any>;
}

export interface TextNodeData extends Omit<FlowNodeData<TextNodeMediaItem>, 'mcpInfo' | 'media_list'> {
  title?: string;
  action?: string;
  media_list?: TextNodeMediaItem[];
  mcpInfo?: TextNodeMcpInfo;
  taskInfo?: Record<string, any>;
  tool?: Record<string, any>;
}

/** 剧本 Markdown 内容项，与文本节点数据类型保持独立。 */
export interface ScreenplayMediaItem extends Record<string, any> {
  /** 旧画布兼容读取；新节点不再将 Markdown 正文写入 value。 */
  value?: string;
  url?: string;
}

/** 剧本节点使用独立类型与 Markdown 内容结构。 */
export interface ScreenplayNodeData extends Omit<FlowNodeData<ScreenplayMediaItem>, 'media_list'> {
  /** Agent 卡片新建节点尚未完成绑定，刷新或重试时仍须先 bind。 */
  screenplay_bind_pending?: boolean;
  media_list?: ScreenplayMediaItem[];
  /** 与 media_list[0].url 对应的云端草稿修订号。 */
  screenplay_md_draft_version?: number;
  /** 仅用于画布预览态的目录数量投影；剧集数据始终以 Script 详情为准。 */
  screenplay_episode_count?: number;
  /** 历史记录及原节点都以首集首文件作为画布预览。 */
  screenplay_first_episode_url?: string;
  /** 最近一次成功保存的历史资产；同一正式版本不重复，不同版本另建完整快照。 */
  screenplay_history_asset_id?: number;
  screenplay_history_version_id?: number;
  screenplay_history_version_key?: string;
  /** 请求结果未知时保留原参数，重试交给服务端幂等去重。 */
  screenplay_history_create_pending?: boolean;
  screenplay_history_pending_version_key?: string;
  /** 从历史记录回插时，服务端 Script 尚未恢复全量目录。 */
  screenplay_history_restore_asset_id?: number;
  screenplay_history_restore_pending?: boolean;
  /** 剧本服务端实体 ID，与专属 Room 一起作为画布快速恢复字段。 */
  script_id?: number | string;
  /** 当前剧本节点独享的 Agent 会话 ID，不复用画布 room_id。 */
  screenplay_session_id?: string;
  /** 已成功登记的项目与会话；后续创建步骤失败时也不重复登记 Room。 */
  screenplay_room_registration?: {
    project_id: string;
    session_id: string;
  };
  /** 同一次创建重试保持幂等键不变，包括 canvas_id 从未接入变为可读取时。 */
  screenplay_create_idempotency_key?: string;
  /** 兼容旧节点中的本地草稿；新编辑只写当前标签页会话存储。 */
  screenplay_local_draft?: {
    text: { title: string; content: string; };
    baseline?: { title: string; content: string; };
  };
}

/** 文本节点创建入参的 data 与 {@link ScriptNodeData} 一致。 */
export type ScriptNodeData = TextNodeData;

interface MeidaCommonItem {
  task_id?: string;
  sub_task_id?: string;
  block_id?: string;
  fileByte?: number;
  material_id?: string;
  assetId?: number;
}

interface ImageMediaItem extends MeidaCommonItem {
  url: string;
  width: number;
  height: number;
  watermark_url: string;
  source?: 'upload' | 'generation' | 'paste' | 'material';
}

export interface ImageNodeData extends FlowNodeData {
  media_list: ImageMediaItem[];
  /** 本地上传与系统产物节点创建阶段的图片加载状态。 */
  status?: 'empty' | 'uploading' | 'loaded' | 'error';
  /** 系统生成图片的来源与编辑任务元数据。 */
  tool?: Record<string, unknown>;
}

interface VideoMediaItem extends MeidaCommonItem {
  url: string;
  width: number;
  height: number;
  watermark_url: string;
  /** @deprecated 旧项目兼容字段，新节点不再写入或读取。 */
  compress_url?: string;
  /** @deprecated 旧项目兼容字段，新节点不再写入或读取。 */
  watermark_compress_url?: string;
  duration: number;
  cover_url: string;
  watermark_cover_url: string;
}

export interface VideoNodeData extends FlowNodeData {
  media_list: VideoMediaItem[];
}

interface AudioMediaItem extends MeidaCommonItem {
  url: string;
  duration: number;
}

export interface AudioNodeData extends FlowNodeData {
  media_list: AudioMediaItem[];
}

export interface ToLineItem {
  toNodeId: string;
  toPortId: 'input' | 'output';
}

interface AddNodeParams<T> {
  /** 未同时传入 x、y 时使用画布默认落点（可视区域中心防重叠） */
  x?: number;
  y?: number;
  data: T;
  toLines?: ToLineItem[];
  /** 新节点命名锚点，连线面板会基于当前节点延续同类型计数。 */
  currentNode?: WorkflowNodeEntity;
  /** 资产库资源可显式指定节点名；未传时继续使用计数器。 */
  nodeName?: string;
  /** 完整复刻已有节点时保留传入数据，不补默认 MCP 或继承公共模型。 */
  preserveData?: boolean;
}

type WorkflowAddNodeType = TVFlowNodeNextTypeEnum | FlowNodeBaseType.GROUP;

/**
 * 判断是否为可深度合并的 plain object（排除数组与 null）。
 */
function isMergeablePlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * 深度合并：incoming 字段优先，defaults 仅补齐缺失或未传入的嵌套字段。
 */
function deepMergeIncomingOverDefaults(
  defaults: Record<string, unknown>,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...defaults };

  Object.entries(incoming).forEach(([key, incomingValue]) => {
    if (incomingValue === undefined) {
      return;
    }

    const defaultValue = result[key];
    if (isMergeablePlainObject(defaultValue) && isMergeablePlainObject(incomingValue)) {
      result[key] = deepMergeIncomingOverDefaults(defaultValue, incomingValue);
      return;
    }

    result[key] = incomingValue;
  });

  return result;
}

/**
 * 非用户上传节点时，按节点类型写入默认模型配置；
 * 若已携带 mcpInfo（如 Agent 落画布），与默认值做嵌套合并而非直接跳过。
 * 特效模板节点（isEffectNode）和片段重拍编辑节点保留调用方状态，不合并默认 parameters。
 */
function applyDefaultMcpInfoIfNeeded<T extends { mcpInfo?: unknown; isCustomer?: boolean; isEffectNode?: boolean; segmentReshoot?: unknown; }>(
  data: T,
  nodeType: ModelNodeType,
): T {
  if (data.isCustomer || data.isEffectNode || data.segmentReshoot) {
    return data;
  }

  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[nodeType];
  const { modelConfig } = useFlowNextStore.getState();

  const initialMcpData = getInitialNodeMcpData(modelConfig?.[modelType]);

  if (!initialMcpData || Object.keys(initialMcpData).length === 0) {
    return data;
  }

  return deepMergeIncomingOverDefaults(
    initialMcpData as Record<string, unknown>,
    data as Record<string, unknown>,
  ) as T;
}

/**
 * 判断创建节点时调用方是否显式传入了 mcpInfo。
 *
 * @param {object} data
 * @return {boolean}
 */
function hasIncomingMcpInfo(data: { mcpInfo?: unknown; }): boolean {
  return Object.prototype.hasOwnProperty.call(data, 'mcpInfo') && data.mcpInfo !== undefined;
}

interface ResolveCreateNodeMcpDataResult {
  nodeData: Record<string, unknown>;
  shouldInheritCommonMcpInfo: boolean;
}

/**
 * 按是否传入 mcpInfo 决定创建节点的模型初始化策略。
 * 传入 mcpInfo 时与默认配置合并；未传入时留空，待节点创建后继承公共 mcpInfo。
 *
 * @param {object} data
 * @param {TVFlowNodeNextTypeEnum} nodeType
 * @return {ResolveCreateNodeMcpDataResult}
 */
function resolveCreateNodeMcpData<T extends { mcpInfo?: unknown; isCustomer?: boolean; isEffectNode?: boolean; segmentReshoot?: unknown; }>(
  data: T,
  nodeType: TVFlowNodeNextTypeEnum,
): ResolveCreateNodeMcpDataResult {
  const MCP_INIT_NODE_TYPES = new Set<TVFlowNodeNextTypeEnum>(
    Object.keys(NODE_TYPE_TO_MODEL_TYPE_MAP) as TVFlowNodeNextTypeEnum[],
  );

  if (!MCP_INIT_NODE_TYPES.has(nodeType) || data.isCustomer || data.isEffectNode || data.segmentReshoot) {
    return {
      nodeData: data as Record<string, unknown>,
      shouldInheritCommonMcpInfo: false,
    };
  }

  if (hasIncomingMcpInfo(data)) {
    return {
      nodeData: applyDefaultMcpInfoIfNeeded(data, nodeType as ModelNodeType) as Record<string, unknown>,
      shouldInheritCommonMcpInfo: false,
    };
  }

  return {
    nodeData: data as Record<string, unknown>,
    shouldInheritCommonMcpInfo: true,
  };
}

/**
 * 新建节点未传入 mcpInfo 时，基于公共模型配置继承 mcpInfo 并写回节点表单。
 *
 * @param {WorkflowNodeEntity} node
 * @param {TVFlowNodeNextTypeEnum} nodeType
 */
function applyInheritedCommonMcpInfo(
  node: WorkflowNodeEntity,
  nodeType: TVFlowNodeNextTypeEnum,
): void {
  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[nodeType as ModelNodeType];
  const { modelConfig, commonModelMcpInfoMap } = useFlowNextStore.getState();
  const targetModelId = commonModelMcpInfoMap[nodeType as ModelNodeType]?.model_id
    ?? getInitialNodeMcpData(modelConfig?.[modelType])?.mcpInfo?.model_id
    ?? '';
  const inheritedMcpInfo = inheritCommonMcpInfo({
    node,
    targetModelId,
  });

  if (!inheritedMcpInfo || !node.form) {
    return;
  }

  const currentData = (node.toJSON()?.data ?? {}) as Record<string, unknown>;
  node.form.updateFormValues({ ...currentData, mcpInfo: inheritedMcpInfo });
}

/**
 * 连线匹配完成后优先沿用同类型缓存的模型和工具，但只选满足当前连入要求的工具。
 * 参数复用普通新建节点的筛选规则，避免把旧节点的 prompt 或不兼容值带入新节点。
 */
function applyInheritedConnectedMcpInfo(
  node: WorkflowNodeEntity,
  nodeType: TVFlowNodeNextTypeEnum,
  commonMcpInfo: FlowNodeMcpInfo | undefined,
): void {
  if (!commonMcpInfo || !node.form) return;

  const currentData = (node.toJSON()?.data ?? {}) as FlowNodeData;
  const { modelConfig } = useFlowNextStore.getState();
  const models = getMediaModelsByNodeType(nodeType, modelConfig);
  if (!models?.length) return;

  const inputCounts = deriveInputTypeCounts(currentData.inputNodeId);
  const commonModel = models.find((model) => model.model_id === commonMcpInfo.model_id);
  const commonTool = commonModel?.tool_list?.find((tool) => (
    tool.node_model_id === commonMcpInfo.node_model_id
    && tool.api_name === commonMcpInfo.api_name
    && isToolCompatible(tool, inputCounts)
  )) ?? findMatchingToolInModel(commonModel, inputCounts, commonMcpInfo.node_model_id);
  const matchedMcpInfo = currentData.mcpInfo;
  const matchedModel = models.find((model) => model.model_id === matchedMcpInfo?.model_id);
  const matchedTool = matchedModel?.tool_list?.find((tool) => (
    tool.node_model_id === matchedMcpInfo?.node_model_id
    && tool.api_name === matchedMcpInfo?.api_name
    && isToolCompatible(tool, inputCounts)
  ));
  const model = commonTool ? commonModel : matchedModel;
  const tool = commonTool ?? matchedTool;
  if (!model || !tool) return;

  node.form.updateFormValues({
    ...currentData,
    mcpInfo: {
      ...matchedMcpInfo,
      model_id: model.model_id,
      node_model_id: tool.node_model_id,
      api_name: tool.api_name,
      parameters: inheritCommonParameters(nodeType, tool.parameters, commonMcpInfo.parameters),
    },
  });
}

/**
 * 在画布上创建节点并建立连线。
 */
function createWorkflowNode<T>(
  ctx: FreeLayoutPluginContext,
  nodeType: WorkflowAddNodeType,
  {
    x,
    y,
    data,
    toLines,
    currentNode,
    nodeName: preferredNodeName,
    preserveData = false,
  }: AddNodeParams<T>,
) {
  if (!ctx?.document) {
    console.warn('[createWorkflowNode] canvas context is not ready');
    return undefined;
  }

  const id = nanoid();
  const position = typeof x === 'number' && typeof y === 'number'
    ? { x, y }
    : ctx.document.getNodeDefaultPosition(nodeType);
  const { nodeData: resolvedNodeData, shouldInheritCommonMcpInfo } = preserveData
    ? {
      nodeData: data as Record<string, unknown>,
      shouldInheritCommonMcpInfo: false,
    }
    : resolveCreateNodeMcpData(
      data as { mcpInfo?: unknown; isCustomer?: boolean; },
      nodeType as TVFlowNodeNextTypeEnum,
    );
  // 创建与连线可能触发节点表单更新，先保留本次新建前的同类型参数。
  const commonMcpInfo = shouldInheritCommonMcpInfo
    ? useFlowNextStore.getState().commonModelMcpInfoMap[nodeType as ModelNodeType]
    : undefined;
  let nodeData: Record<string, any> = resolvedNodeData;
  const fallbackName = nodeType === FlowNodeBaseType.GROUP
    ? 'group'
    : NODE_NAME_CONFIG[nodeType] ?? 'roboneo_web_node';
  const nodeName = typeof preferredNodeName === 'string' && preferredNodeName.trim()
    ? preferredNodeName.trim()
    : getNextNodeNameByCurrentType({
      currentNode,
      nextNodeType: nodeType as TVFlowNodeNextTypeEnum,
      fallbackName,
    });

  nodeData = {
    ...nodeData,
    name: nodeName,
  };

  const node = ctx.document.createWorkflowNode({
    id,
    type: nodeType,
    meta: {
      position,
    },
    data: nodeData,
  });

  applyToLines(ctx, id, toLines);

  // 未传入 mcpInfo 时，先按连线匹配可用模型与工具，再继承同类型缓存中的兼容参数。
  if (shouldInheritCommonMcpInfo) {
    if (toLines?.length) {
      const latestNodeData = (node.toJSON()?.data ?? {}) as FlowNodeData;
      autoMatchAndUpdateNode(node, latestNodeData.inputNodeId);
      if (isAutoMatchMediaNodeType(nodeType as TVFlowNodeNextTypeEnum)) {
        applyInheritedConnectedMcpInfo(node, nodeType as TVFlowNodeNextTypeEnum, commonMcpInfo);
      } else {
        applyInheritedCommonMcpInfo(node, nodeType as TVFlowNodeNextTypeEnum);
      }
    } else {
      applyInheritedCommonMcpInfo(node, nodeType as TVFlowNodeNextTypeEnum);
    }
  }

  return node;
}

/**
 * 根据 toLines 配置，为刚创建的节点批量建立连线。
 *
 * toPortId 是目标节点的端口，当前节点自动取反向端口。
 * createLine 的 from→to 代表连线数据流方向（output→input），
 * 因此需要根据端口角色决定谁做 from、谁做 to。
 */
function applyToLines(
  ctx: FreeLayoutPluginContext,
  currentNodeId: string,
  toLines?: ToLineItem[],
) {
  if (!toLines?.length) return;
  toLines.forEach(({ toNodeId, toPortId }) => {
    const currentPort = toPortId === 'input' ? 'output' : 'input';
    if (currentPort === 'output') {
      ctx.document.linesManager.createLine({
        from: currentNodeId,
        fromPort: 'output',
        to: toNodeId,
        toPort: 'input',
      });
    } else {
      ctx.document.linesManager.createLine({
        from: toNodeId,
        fromPort: 'output',
        to: currentNodeId,
        toPort: 'input',
      });
    }
  });
}

/**
 * 在 Flow Next 画布上新增文本输入节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addTextNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<TextNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.TEXT_NODE, params);
}

/**
 * 在 Flow Next 画布上新增独立的剧本节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addScreenplayNode(
  ctx: FreeLayoutPluginContext,
  params: AddNodeParams<ScreenplayNodeData>,
) {
  const { smartcatTranslationText } = useLanguageStore.getState().languageStore;
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.SCREENPLAY_NODE, {
    ...params,
    nodeName: params.nodeName ?? smartcatTranslationText((value) => value.script_1),
  });
}

/**
 * 在 Flow Next 画布上新增图片输入节点。
 * 必须使用 {@link WorkflowDocument.createWorkflowNode}，以便触发 ADD_NODE、同步 Transform 与表单引擎；直接 `addNode` 不会走完整工作流创建链路，画布可能无变化。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addImageNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<ImageNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.IMAGE_NODE, params);
}

/**
 * 在 Flow Next 画布上新增视频输入节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addVideoNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<VideoNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.VIDEO_NODE, params);
}

/**
 * 在 Flow Next 画布上新增音频输入节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addAudioNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<AudioNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.AUDIO_NODE, params);
}

/**
 * 在 Flow Next 画布上新增脚本输入节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addScriptNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<ScriptNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.SCRIPT_NODE, params);
}

/**
 * 在 Flow Next 画布上新增导演台节点。
 *
 * 导演台不是模型任务节点，不进入 MCP 默认初始化集合，只写入可持久化的导演台 JSON 状态。
 * 位置继续使用节点面板传入的 x/y，避免新类型单独走默认落点。
 */
export function addDirectorConsoleNode(
  ctx: FreeLayoutPluginContext,
  params: AddNodeParams<Partial<DirectorConsoleNodeData>>,
) {
  const { smartcatTranslationText } = useLanguageStore.getState().languageStore;
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE, {
    ...params,
    nodeName: params.nodeName ?? smartcatTranslationText((v) => v.director_s_booth),
    data: {
      ...createDirectorConsoleDataPatch(createDefaultDirectorConsoleState()),
      ...params.data,
    },
  });
}

/**
 * 在 Flow Next 画布上新增容器节点，名称由统一计数器生成。
 */
export function addContainerNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<Record<string, any>>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.CONTAINER_NODE, params);
}

/**
 * 在 Flow Next 画布上新增群组节点，供需要保留完整群组数据的创建场景使用。
 */
export function addGroupNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<Record<string, any>>) {
  return createWorkflowNode(ctx, FlowNodeBaseType.GROUP, params);
}

export const External_Media_Storeage_Key = 'team_flow_external_media_storage_key';

type ExternalMediaItem = {
  name: string;
  type: TVFlowNodeNextTypeEnum.IMAGE_NODE | TVFlowNodeNextTypeEnum.VIDEO_NODE | TVFlowNodeNextTypeEnum.AUDIO_NODE;
};
export type ExternalCacheDataItem =
  | (ImageMediaItem & ExternalMediaItem)
  | (VideoMediaItem & ExternalMediaItem)
  | (AudioMediaItem & ExternalMediaItem);

interface ExternalMediaNodeSize {
  width: number;
  height: number;
}

const EXTERNAL_MEDIA_NODE_GAP_Y = 48;
const EXTERNAL_AUDIO_NODE_WIDTH = 240;
const EXTERNAL_AUDIO_NODE_HEIGHT = 160;
const EXTERNAL_MEDIA_NODE_HEADER_HEIGHT = 40;
const EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_WIDTH = 360;
const EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_HEIGHT = 280;

/**
 * 添加外部资源到缓存。
 *
 * @export
 * @param {ExternalCacheDataItem[]} mediaList
 */
export function addExternalMedias(mediaList: ExternalCacheDataItem[]) {
  sessionStorage.setItem(External_Media_Storeage_Key, JSON.stringify(mediaList));
}

/**
 * 估算外部媒体节点的画布占位，和资产库插入 TeamFlow 时的避让规则保持一致。
 */
function getExternalMediaNodeSize(item: ExternalCacheDataItem): ExternalMediaNodeSize {
  if (item.type === TVFlowNodeNextTypeEnum.AUDIO_NODE) {
    return {
      width: EXTERNAL_AUDIO_NODE_WIDTH,
      height: EXTERNAL_AUDIO_NODE_HEIGHT,
    };
  }

  const rawWidth = typeof item.width === 'number' ? item.width : undefined;
  const rawHeight = typeof item.height === 'number' ? item.height : undefined;
  if (rawWidth == null || rawHeight == null || rawWidth <= 0 || rawHeight <= 0) {
    return {
      width: EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_WIDTH,
      height: EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_HEIGHT + EXTERNAL_MEDIA_NODE_HEADER_HEIGHT,
    };
  }

  const scale = Math.min(
    EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_WIDTH / rawWidth,
    EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_HEIGHT / rawHeight,
    1,
  );

  return {
    width: Math.max(1, Math.round(rawWidth * scale)),
    height: Math.max(1, Math.round(rawHeight * scale)) + EXTERNAL_MEDIA_NODE_HEADER_HEIGHT,
  };
}

/**
 * 外部媒体默认落点沿当前画布整体边界下方逐个追加，与资产库弹窗插入工作流保持一致。
 */
function getExternalMediaNodePosition(
  allNodesBounds: ReturnType<typeof getAllNodesBounds> | undefined,
  lastNodeSize: ExternalMediaNodeSize | undefined,
  lastPosition: { x: number; y: number; } | undefined,
) {
  return {
    x: allNodesBounds?.left ?? 0,
    y: lastPosition
      ? lastPosition.y + (lastNodeSize?.height ?? 0) + EXTERNAL_MEDIA_NODE_GAP_Y
      : (allNodesBounds?.bottom ?? 0) + EXTERNAL_MEDIA_NODE_GAP_Y,
  };
}

/**
 * 添加外部资源到画布，转为节点。
 * 节点位置沿当前画布整体边界下方纵向追加，与资产库弹窗插入工作流保持一致。
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @return {*}
 */
export function renderExternalMedias(ctx: FreeLayoutPluginContext) {
  try {
    const cacheData = sessionStorage.getItem(External_Media_Storeage_Key);
    if (cacheData) {
      const cacheDataList: ExternalCacheDataItem[] = JSON.parse(cacheData);
      const allNodesBounds = getAllNodesBounds(ctx.document.getAllNodes());
      let lastNodeSize: ExternalMediaNodeSize | undefined;
      let lastPosition: { x: number; y: number; } | undefined;
      let lastAddedNode: ReturnType<typeof addImageNode> | undefined;

      for (let i = 0; i < cacheDataList.length; i += 1) {
        const item = cacheDataList[i];
        const { type, ...mediaItem } = item;
        const itemNodeSize = getExternalMediaNodeSize(item);
        const { x, y } = getExternalMediaNodePosition(allNodesBounds, lastNodeSize, lastPosition);
        lastPosition = { x, y };
        lastNodeSize = itemNodeSize;

        if (type === TVFlowNodeNextTypeEnum.IMAGE_NODE) {
          lastAddedNode = addImageNode(ctx, {
            x,
            y,
            data: {
              isCustomer: true,
              media_list: [mediaItem],
            },
          });
        }
        if (type === TVFlowNodeNextTypeEnum.VIDEO_NODE) {
          lastAddedNode = addVideoNode(ctx, {
            x,
            y,
            data: {
              isCustomer: true,
              media_list: [mediaItem as VideoMediaItem],
            },
          });
        }
        if (type === TVFlowNodeNextTypeEnum.AUDIO_NODE) {
          lastAddedNode = addAudioNode(ctx, {
            x,
            y,
            data: {
              isCustomer: true,
              media_list: [mediaItem as AudioMediaItem],
            },
          });
        }
      }

      if (lastAddedNode?.bounds) {
        ctx.playground.scrollToView({ bounds: lastAddedNode.bounds });
      }

      sessionStorage.removeItem(External_Media_Storeage_Key);
      return true;
    }
    return false;
  } catch (error) {
    sessionStorage.removeItem(External_Media_Storeage_Key);
    return false;
  }
}
